
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isAndroidTWA } from "@/utils/runtimeContext";
import AndroidNeutralNotice from "@/components/legal/AndroidNeutralNotice";

interface SubscriptionCardProps {
  plan: "trial15" | "basic" | "premium" | "vip";
  title: string;
  price: number;
  billingPeriod: "biweekly" | "monthly" | "quarterly" | "annual";
  features: string[];
  isPopular?: boolean;
  isCurrentPlan?: boolean;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  plan,
  title,
  price,
  billingPeriod,
  features,
  isPopular = false,
  isCurrentPlan = false
}) => {
  const { isAuthenticated, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  if (isAndroidTWA()) {
    return <AndroidNeutralNotice />;
  }

  // Links Stripe mantidos ocultos (não excluídos) — página Planos agora usa Ton (igual à /pagamento)
  const stripeLinks: Record<string, string> = {
    trial15: "https://payment-link-v3.ton.com.br/pl_jmE3QdVoaOkNQxyT0qFpPWqpbYzBAxXw",
    basic: "https://buy.stripe.com/bJe28r2Q49Fp7K67sZak002",
    premium: "https://buy.stripe.com/00w28r2Q49Fp3tQbJfak001",
    vip: "https://buy.stripe.com/cNi4gz2Q4aJtd4q7sZak003",
  };

  const tonLinks: Record<string, string> = {
    trial15: "https://payment-link-v3.ton.com.br/pl_jmE3QdVoaOkNQxyT0qFpPWqpbYzBAxXw",
    basic: "https://payment-link-v3.ton.com.br/pl_ZQwzvp2lJo7NmVQHDxSzXV5YLBM6rP0A",
    premium: "https://payment-link-v3.ton.com.br/pl_MOmQDl0pqL34xMDFQOfyRWdVvkN1aYZg",
    vip: "https://payment-link-v3.ton.com.br/pl_L70p9dexNmVXEJeJTLU3AgoaMrlk5Zb2",
  };

  const billingLabels = {
    biweekly: "15 dias",
    monthly: "mês",
    quarterly: "trimestre",
    annual: "ano"
  };

  const handleSubscribe = () => {
    console.log("Abrindo checkout Ton:", { plan });
    const link = tonLinks[plan] ?? stripeLinks[plan];
    if (link) {
      window.open(link, "_blank");
    }
  };

  const isActive = user?.plan === plan && user?.subscriptionEndDate && 
    new Date(user.subscriptionEndDate) > new Date();

  return (
    <Card className={`relative ${isPopular ? 'border-primary shadow-lg' : ''} ${isCurrentPlan || isActive ? 'ring-2 ring-primary' : ''}`}>
      {isPopular && (
        <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
          <Star className="w-3 h-3 mr-1" />
          Mais Popular
        </Badge>
      )}
      
      {(isCurrentPlan || isActive) && (
        <Badge className="absolute -top-3 right-4 bg-green-500">
          Seu Plano
        </Badge>
      )}

      <CardHeader className="text-center">
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>
          <span className="text-3xl font-bold">R$ {price.toFixed(2)}</span>
          <span className="text-muted-foreground">/{billingLabels[billingPeriod]}</span>
        </CardDescription>
      </CardHeader>

      <CardContent>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter>
        <Button 
          className="w-full" 
          onClick={handleSubscribe}
          disabled={isLoading || isCurrentPlan || isActive}
          variant={isPopular ? "default" : "outline"}
        >
          {isLoading ? "Processando..." : 
           isCurrentPlan || isActive ? "Plano Atual" : 
           `Assinar ${title}`}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default SubscriptionCard;
